import { Navigate, type RouteObject } from "react-router-dom";

import {
  ChecklistModel,
  DocumentCategory,
  EquipmentCategories,
  FinancialCategories,
  MaterialCategories,
  MeasurementUnits,
  Occurrence,
  Partners,
  PaymentMethods,
  PhotoTagsSettings,
  ProjectBudgetStatuses,
  ProjectClientBudgetStatuses,
  ProjectPriorities,
  ProjectStatuses,
  ProjectTypes,
  ServiceFrontDeadlineStatuses,
  ServiceFrontModel,
  SettingsDiaryReportModels,
  SettingsEquipments,
  SettingsGeneral,
  SettingsHome,
  SettingsMaterials,
  SettingsOrganization,
  SettingsBilling,
  SettingsPermissionTemplates,
  SettingsServiceTaxCodes,
  SettingsUsers,
  Worker,
  WorkerRoles,
} from "@/router/lazyPages/settings";
import { NotFound } from "@/router/lazyPages/shared";
import {
  ChecklistModelEditRedirect,
  DocumentCategoryEditRedirect,
  EquipmentCategoryEditRedirect,
  EquipmentEditRedirect,
  FinancialCategoryEditRedirect,
  LegacyOccurrenceEditRedirect,
  LegacyServiceTypeEditRedirect,
  LegacyWorkerEditRedirect,
  MaterialCategoryEditRedirect,
  MaterialEditRedirect,
  MeasurementUnitEditRedirect,
  OccurrenceEditRedirect,
  PartnerEditRedirect,
  PaymentMethodEditRedirect,
  PhotoTagEditRedirect,
  ProjectBudgetStatusEditRedirect,
  ProjectClientBudgetStatusEditRedirect,
  ProjectPriorityEditRedirect,
  ProjectStatusEditRedirect,
  ProjectTypeEditRedirect,
  PermissionTemplateEditRedirect,
  ServiceFrontDeadlineStatusEditRedirect,
  ServiceFrontModelEditRedirect,
  ServiceTaxCodeEditRedirect,
  UsersEditRedirect,
  WorkerEditRedirect,
  WorkerRoleEditRedirect,
} from "./redirects";
import { skeletonRoute } from "@/router/skeletonRoute";
import { SkeletonVariant } from "@/types/routeSkeleton";

export const settingsLegacyRedirects: RouteObject[] = [
  {
    path: "/project-service-fronts-models",
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  {
    path: "/service-types",
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  {
    path: "/team",
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  { path: "/workers", element: <Navigate to="/settings/workers" replace /> },
  {
    path: "/occurrences",
    element: <Navigate to="/settings/occurrences" replace />,
  },
  {
    path: "/occurrences/new",
    element: <Navigate to="/settings/occurrences" replace />,
  },
  {
    path: "/occurrences/:id/edit",
    element: <LegacyOccurrenceEditRedirect />,
  },
  { path: "/materials", element: <Navigate to="/settings/materials" replace /> },
  { path: "/equipments", element: <Navigate to="/settings/equipments" replace /> },
];

export const settingsShellRoutes: RouteObject[] = [
  { index: true, element: <SettingsHome /> },
  {
    path: "general",
    handle: { permission: "settings.organization" },
    element: <SettingsGeneral />,
  },
  {
    path: "organization",
    handle: { permission: "settings.organization" },
    element: <SettingsOrganization />,
  },
  {
    path: "billing",
    element: <SettingsBilling />,
  },
  {
    path: "organization/edit",
    handle: { permission: "settings.organization" },
    element: <Navigate to="/settings/organization?edit=1" replace />,
  },
  {
    path: "fiscal",
    handle: { permission: "settings.organization" },
    element: <Navigate to="/settings/organization" replace />,
  },
  {
    path: "users",
    handle: { permission: "settings.users" },
    element: <SettingsUsers />,
  },
  {
    path: "users/invite",
    handle: { permission: "settings.users" },
    element: <Navigate to="/settings/users?invite=1" replace />,
  },
  {
    path: "users/:userId/edit",
    handle: { permission: "settings.users" },
    element: <UsersEditRedirect />,
  },
  {
    path: "permissions",
    handle: { permission: "templates.manage" },
    element: <SettingsPermissionTemplates />,
  },
  {
    path: "permissions/new",
    handle: { permission: "templates.manage" },
    element: <Navigate to="/settings/permissions" replace />,
  },
  {
    path: "permissions/:id/edit",
    handle: { permission: "templates.manage" },
    element: <PermissionTemplateEditRedirect />,
  },
  skeletonRoute({
    path: "diary-report-models",
    permission: "project.projectReport.manageModels",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <SettingsDiaryReportModels />,
  }),
  {
    path: "service-front-models",
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  skeletonRoute({
    path: "project-service-fronts-models",
    permission: "settings.serviceFrontModels",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ServiceFrontModel />,
  }),
  {
    path: "project-service-fronts-models/new",
    handle: { permission: "settings.serviceFrontModels" },
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  {
    path: "project-service-fronts-models/:id/edit",
    handle: { permission: "settings.serviceFrontModels" },
    element: <ServiceFrontModelEditRedirect />,
  },
  skeletonRoute({
    path: "checklist-models",
    permission: "settings.checklistModels",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ChecklistModel />,
  }),
  {
    path: "checklist-models/new",
    handle: { permission: "settings.checklistModels" },
    element: <Navigate to="/settings/checklist-models" replace />,
  },
  {
    path: "checklist-models/:id/edit",
    handle: { permission: "settings.checklistModels" },
    element: <ChecklistModelEditRedirect />,
  },
  skeletonRoute({
    path: "workers",
    permission: "settings.workers",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <Worker />,
  }),
  {
    path: "workers/new",
    handle: { permission: "settings.workers" },
    element: <Navigate to="/settings/workers" replace />,
  },
  {
    path: "workers/:id/edit",
    handle: { permission: "settings.workers" },
    element: <WorkerEditRedirect />,
  },
  skeletonRoute({
    path: "workers/roles",
    permission: "settings.workers",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <WorkerRoles />,
  }),
  {
    path: "workers/roles/new",
    handle: { permission: "settings.workers" },
    element: <Navigate to="/settings/workers/roles" replace />,
  },
  {
    path: "workers/roles/:id/edit",
    handle: { permission: "settings.workers" },
    element: <WorkerRoleEditRedirect />,
  },
  skeletonRoute({
    path: "occurrences",
    permission: "settings.occurrences",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <Occurrence />,
  }),
  {
    path: "occurrences/new",
    handle: { permission: "settings.occurrences" },
    element: <Navigate to="/settings/occurrences" replace />,
  },
  {
    path: "occurrences/:id/edit",
    handle: { permission: "settings.occurrences" },
    element: <OccurrenceEditRedirect />,
  },
  skeletonRoute({
    path: "document-categories",
    permission: "settings.documentCategories",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <DocumentCategory />,
  }),
  {
    path: "document-categories/new",
    handle: { permission: "settings.documentCategories" },
    element: <Navigate to="/settings/document-categories" replace />,
  },
  {
    path: "document-categories/:id/edit",
    handle: { permission: "settings.documentCategories" },
    element: <DocumentCategoryEditRedirect />,
  },
  skeletonRoute({
    path: "photo-tags",
    permission: "settings.photoTags",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <PhotoTagsSettings />,
  }),
  {
    path: "photo-tags/new",
    handle: { permission: "settings.photoTags" },
    element: <Navigate to="/settings/photo-tags" replace />,
  },
  {
    path: "photo-tags/:id/edit",
    handle: { permission: "settings.photoTags" },
    element: <PhotoTagEditRedirect />,
  },
  skeletonRoute({
    path: "partners",
    permission: "settings.partners",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <Partners />,
  }),
  {
    path: "partners/new",
    handle: { permission: "settings.partners" },
    element: <Navigate to="/settings/partners" replace />,
  },
  {
    path: "partners/:id/edit",
    handle: { permission: "settings.partners" },
    element: <PartnerEditRedirect />,
  },
  skeletonRoute({
    path: "financial-categories",
    permission: "settings.financeLookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <FinancialCategories />,
  }),
  {
    path: "financial-categories/new",
    handle: { permission: "settings.financeLookups" },
    element: <Navigate to="/settings/financial-categories" replace />,
  },
  {
    path: "financial-categories/:id/edit",
    handle: { permission: "settings.financeLookups" },
    element: <FinancialCategoryEditRedirect />,
  },
  skeletonRoute({
    path: "payment-methods",
    permission: "settings.financeLookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <PaymentMethods />,
  }),
  {
    path: "payment-methods/new",
    handle: { permission: "settings.financeLookups" },
    element: <Navigate to="/settings/payment-methods" replace />,
  },
  {
    path: "payment-methods/:id/edit",
    handle: { permission: "settings.financeLookups" },
    element: <PaymentMethodEditRedirect />,
  },
  skeletonRoute({
    path: "service-tax-codes",
    permission: "settings.fiscal",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <SettingsServiceTaxCodes />,
  }),
  {
    path: "service-tax-codes/new",
    handle: { permission: "settings.fiscal" },
    element: <Navigate to="/settings/service-tax-codes" replace />,
  },
  {
    path: "service-tax-codes/:id/edit",
    handle: { permission: "settings.fiscal" },
    element: <ServiceTaxCodeEditRedirect />,
  },
  skeletonRoute({
    path: "project-priorities",
    permission: "settings.lookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectPriorities />,
  }),
  {
    path: "project-priorities/new",
    handle: { permission: "settings.lookups" },
    element: <Navigate to="/settings/project-priorities" replace />,
  },
  {
    path: "project-priorities/:id/edit",
    handle: { permission: "settings.lookups" },
    element: <ProjectPriorityEditRedirect />,
  },
  skeletonRoute({
    path: "project-types",
    permission: "settings.lookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectTypes />,
  }),
  {
    path: "project-types/new",
    handle: { permission: "settings.lookups" },
    element: <Navigate to="/settings/project-types" replace />,
  },
  {
    path: "project-types/:id/edit",
    handle: { permission: "settings.lookups" },
    element: <ProjectTypeEditRedirect />,
  },
  skeletonRoute({
    path: "project-statuses",
    permission: "settings.lookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectStatuses />,
  }),
  {
    path: "project-statuses/:status/edit",
    handle: { permission: "settings.lookups" },
    element: <ProjectStatusEditRedirect />,
  },
  skeletonRoute({
    path: "project-budget-statuses",
    permission: "settings.lookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectBudgetStatuses />,
  }),
  {
    path: "project-budget-statuses/:status/edit",
    handle: { permission: "settings.lookups" },
    element: <ProjectBudgetStatusEditRedirect />,
  },
  skeletonRoute({
    path: "project-client-budget-statuses",
    permission: "settings.lookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectClientBudgetStatuses />,
  }),
  {
    path: "project-client-budget-statuses/:status/edit",
    handle: { permission: "settings.lookups" },
    element: <ProjectClientBudgetStatusEditRedirect />,
  },
  {
    path: "service-front-deadline-statuses",
    element: <Navigate to="/settings/project-service-fronts-deadline-statuses" replace />,
  },
  skeletonRoute({
    path: "project-service-fronts-deadline-statuses",
    permission: "settings.lookups",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ServiceFrontDeadlineStatuses />,
  }),
  {
    path: "project-service-fronts-deadline-statuses/:status/edit",
    handle: { permission: "settings.lookups" },
    element: <ServiceFrontDeadlineStatusEditRedirect />,
  },
  {
    path: "materials",
    children: [
      {
        index: true,
        handle: {
          permission: "settings.materials",
          skeleton: {
            skeletonVariant: SkeletonVariant.Embedded,
          },
        },
        element: <SettingsMaterials />,
      },
      skeletonRoute({
        path: "categories",
        permission: "settings.materials",
        skeleton: {
          skeletonVariant: SkeletonVariant.Embedded,
        },
        element: <MaterialCategories />,
      }),
      {
        path: "categories/new",
        handle: { permission: "settings.materials" },
        element: <Navigate to="/settings/materials/categories" replace />,
      },
      {
        path: "categories/:id/edit",
        handle: { permission: "settings.materials" },
        element: <MaterialCategoryEditRedirect />,
      },
      skeletonRoute({
        path: "measurement-units",
        permission: "settings.materials",
        skeleton: {
          skeletonVariant: SkeletonVariant.Embedded,
        },
        element: <MeasurementUnits />,
      }),
      {
        path: "measurement-units/new",
        handle: { permission: "settings.materials" },
        element: <Navigate to="/settings/materials/measurement-units" replace />,
      },
      {
        path: "measurement-units/:id/edit",
        handle: { permission: "settings.materials" },
        element: <MeasurementUnitEditRedirect />,
      },
      {
        path: "new",
        handle: { permission: "settings.materials" },
        element: <Navigate to="/settings/materials" replace />,
      },
      {
        path: ":id/edit",
        handle: { permission: "settings.materials" },
        element: <MaterialEditRedirect />,
      },
      {
        path: "items",
        element: <Navigate to="/settings/materials" replace />,
      },
      { path: "*", element: <NotFound /> },
    ],
  },
  {
    path: "equipments",
    children: [
      {
        index: true,
        handle: {
          permission: "settings.equipments",
          skeleton: {
            skeletonVariant: SkeletonVariant.Embedded,
          },
        },
        element: <SettingsEquipments />,
      },
      skeletonRoute({
        path: "categories",
        permission: "settings.equipments",
        skeleton: {
          skeletonVariant: SkeletonVariant.Embedded,
        },
        element: <EquipmentCategories />,
      }),
      {
        path: "categories/new",
        handle: { permission: "settings.equipments" },
        element: <Navigate to="/settings/equipments/categories" replace />,
      },
      {
        path: "categories/:id/edit",
        handle: { permission: "settings.equipments" },
        element: <EquipmentCategoryEditRedirect />,
      },
      {
        path: "new",
        handle: { permission: "settings.equipments" },
        element: <Navigate to="/settings/equipments" replace />,
      },
      {
        path: ":id/edit",
        handle: { permission: "settings.equipments" },
        element: <EquipmentEditRedirect />,
      },
      {
        path: "items",
        element: <Navigate to="/settings/equipments" replace />,
      },
      { path: "*", element: <NotFound /> },
    ],
  },
  { path: "*", element: <NotFound /> },
];

export const settingsFormRoutes: RouteObject[] = [
  {
    path: "/project-service-fronts-models/new",
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  {
    path: "/project-service-fronts-models/:id/edit",
    element: <LegacyServiceTypeEditRedirect />,
  },
  {
    path: "/service-types/new",
    element: <Navigate to="/settings/project-service-fronts-models" replace />,
  },
  {
    path: "/service-types/:id/edit",
    element: <LegacyServiceTypeEditRedirect />,
  },
  {
    path: "/workers/new",
    element: <Navigate to="/settings/workers" replace />,
  },
  {
    path: "/workers/:id/edit",
    element: <LegacyWorkerEditRedirect />,
  },
];
